import { useContext, useEffect, useRef, useState } from 'react';
import { AuthContext } from '../App';
import { getPushPermissionState, enablePushNotifications, recoverPushRegistrationSilently } from '../lib/pushNotifications';
import { readCurrentPushRegistration, pushAccount } from '../lib/pushOwnership';
import { beginPushPromptAction } from '../lib/pushPromptManager';

// Explicit setup is independent of every automatic reminder/session/cooldown.
export default function PersistentPushAction() {
  const { user, token } = useContext(AuthContext) || {};
  const epoch = useRef(0), reads = useRef(0), busyRef = useRef(false);
  const [state, setState] = useState(null), [busy, setBusy] = useState(false);
  const [guidance, setGuidance] = useState(false), [message, setMessage] = useState('');
  const native = (() => { try { return window.Capacitor?.getPlatform?.() === 'ios' && window.Capacitor?.isNativePlatform?.() === true; } catch { return false; } })();
  useEffect(() => {
    const generation = ++epoch.current;
    busyRef.current = false; setBusy(false); setState(null); setGuidance(false); setMessage('');
    const check = async () => {
      const read = ++reads.current;
      const current = () => epoch.current === generation && reads.current === read;
      if (!native || !user?.id || !token || pushAccount(token) !== user.id) return;
      const permission = await getPushPermissionState();
      if (!current()) return;
      const registered = permission === 'granted' ? await readCurrentPushRegistration(token, current) : null;
      if (current()) setState({ account: user.id, token, permission, registered });
    };
    const foreground = () => { if (!busyRef.current && document.visibilityState === 'visible') check(); };
    check(); window.addEventListener('focus', foreground); window.addEventListener('online', foreground);
    document.addEventListener('visibilitychange', foreground);
    return () => { ++epoch.current; window.removeEventListener('focus', foreground); window.removeEventListener('online', foreground); document.removeEventListener('visibilitychange', foreground); };
  }, [user?.id, token, native]);
  if (!native || !state || state.account !== user?.id || state.token !== token || (state.permission === 'granted' && state.registered === true)) return null;
  const label = state.permission === 'prompt' || state.permission === 'denied' ? 'Enable Notifications'
    : state.permission === 'granted' ? 'Retry notification setup' : 'Check notification status';
  const act = async () => {
    if (busyRef.current) return;
    const generation = epoch.current, read = ++reads.current, current = () => epoch.current === generation && reads.current === read;
    const finishReminderAction = beginPushPromptAction();
    busyRef.current = true; setBusy(true); setMessage('');
    try {
      const permission = await getPushPermissionState();
      if (!current()) return;
      if (permission === 'denied') { setState({ account: user.id, token, permission, registered: null }); setGuidance(true); return; }
      // An unknown-state action only rechecks, even if the refreshed state is prompt.
      if (!['prompt', 'granted'].includes(state.permission) || !['prompt', 'granted'].includes(permission) || (state.permission === 'granted' && permission !== 'granted')) {
        const registered = permission === 'granted' ? await readCurrentPushRegistration(token, current) : null;
        if (current()) { setState({ account: user.id, token, permission, registered }); if (permission === 'unknown' || permission === 'unavailable') setMessage('Notification status could not be checked. Please try again.'); }
        return;
      }
      const result = permission === 'prompt'
        ? await enablePushNotifications({ requestPermission: true, isCurrent: current })
        : await recoverPushRegistrationSilently();
      if (!current()) return;
      const nextPermission = await getPushPermissionState();
      if (!current()) return;
      const registered = nextPermission === 'granted' ? await readCurrentPushRegistration(token, current) : null;
      if (!current()) return;
      setState({ account: user.id, token, permission: nextPermission, registered });
      if (nextPermission === 'denied') setGuidance(true);
      else if (result.code !== 'registered' || registered !== true) setMessage('Notification setup is not yet confirmed. You can check or retry here.');
    } catch { if (current()) setMessage('Notification setup could not be checked. Please try again.'); }
    finally { finishReminderAction(); if (current()) { busyRef.current = false; setBusy(false); } }
  };
  return <section className="mb-3 text-sm text-zinc-500" aria-label="Notification setup" data-testid="persistent-push-action">
    <button type="button" onClick={act} disabled={busy} className="min-h-11 text-sm underline underline-offset-4 disabled:opacity-50">{busy ? 'Checking notification setup…' : label}</button>
    {guidance && <div className="mb-2 rounded-lg border border-zinc-200 bg-zinc-50 p-3" role="status">
      <h2 className="font-medium text-zinc-800">Notifications are turned off</h2>
      <p className="mt-1">To receive invitations and game updates, open your iPhone’s Settings, find Open Runs, select Notifications and turn on Allow Notifications.</p>
      <button type="button" onClick={() => setGuidance(false)} className="mt-2 min-h-11 underline">Dismiss guidance</button>
    </div>}
    {message && <p role="status">{message}</p>}
  </section>;
}
